import React from 'react';

// Catches lazy-chunk load failures (stale chunk after a rebuild/redeploy,
// poisoned browser cache) and recovers with a single full reload instead of
// a dead error screen. A manual refresh button appears if it still fails.
const isChunkError = (err) => {
  const msg = String((err && err.message) || '');
  return (
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('dynamically imported module')
  );
};

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, fatal: false };
  }

  static getDerivedStateFromError(error) {
    if (isChunkError(error)) return { hasError: true, fatal: false };
    return { hasError: true, fatal: true };
  }

  componentDidCatch(error) {
    if (!isChunkError(error)) return;
    try {
      if (!sessionStorage.getItem('chunk-reload-once')) {
        sessionStorage.setItem('chunk-reload-once', '1');
        window.location.reload();
        return;
      }
    } catch {
      window.location.reload();
      return;
    }
    this.setState({ fatal: true });
  }

  handleManualReload = () => {
    try {
      sessionStorage.removeItem('chunk-reload-once');
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.fatal) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-bold text-gray-900">Page failed to load</h2>
            <p className="text-sm text-gray-600 mt-2">
              Part of the app could not be loaded (stale cached files). Reload to fetch the latest version.
            </p>
            <button
              onClick={this.handleManualReload}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    return this.props.children;
  }
}
